// packages/ui/src/auth/AuthBrandPanel.tsx
import type { CSSProperties, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface AuthBrandFeature {
  icon: LucideIcon;
  label: string;
}

export interface AuthBrandPanelProps {
  /** Wordmark or logo, rendered inside the home link. Text should be white on the panel. */
  brand: ReactNode;
  homeHref: string;
  /** Small label next to the brand, for apps that name the product (for example a console). */
  badge?: string;
  title: ReactNode;
  description: string;
  features: AuthBrandFeature[];
  footer: ReactNode;
  /** Extra classes for the title, for an app that sets its heading font (font-display). */
  headingClassName?: string;
  /**
   * Surface colour. Omit to get the platform teal-700. A tenant colour replaces it, and the
   * caller owns the contrast: white text must stay AA (4.5:1) on whatever is passed.
   */
  surfaceColor?: string;
  /** Colour of the large corner circle. Omit for the platform orange-500. */
  accentColor?: string;
}

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const FUNCTIONAL_COLOR = /^(?:rgba?|hsla?)\([0-9.,%/ ]+\)$/;

// A tenant colour ends up in an inline style attribute, which server rendering writes out
// verbatim, so anything but a plain hex or rgb()/hsl() value (digits, dots, commas, spaces,
// percent, slash) is dropped and the platform look is used instead.
function safeColor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const color = value.trim();
  return HEX_COLOR.test(color) || FUNCTIONAL_COLOR.test(color) ? color : undefined;
}

// The left brand panel of every auth screen: teal-700 patterned surface (the landing hero's
// look), an orange circle, boxed feature icons. Hidden below `lg`, where AuthMobileBar takes
// over. The colour props only emit inline styles when passed, so the default markup stays
// the same class list the dashboard shipped. `.bg-pattern` comes from styles.css.
export function AuthBrandPanel({
  brand,
  homeHref,
  badge,
  title,
  description,
  features,
  footer,
  headingClassName,
  surfaceColor: surfaceInput,
  accentColor: accentInput,
}: AuthBrandPanelProps) {
  const surfaceColor = safeColor(surfaceInput);
  const accentColor = safeColor(accentInput);
  const surfaceStyle: CSSProperties | undefined = surfaceColor ? { backgroundColor: surfaceColor } : undefined;
  const accentStyle: CSSProperties | undefined = accentColor ? { backgroundColor: accentColor } : undefined;
  // The second circle is the surface lifted a little, so it follows a custom surface.
  const shadeStyle: CSSProperties | undefined = surfaceColor
    ? { backgroundColor: `color-mix(in srgb, white 12%, ${surfaceColor})` }
    : undefined;

  const homeLink = (
    <a href={homeHref} className="flex items-center">
      <span className="text-2xl font-bold tracking-tight text-white">{brand}</span>
    </a>
  );

  return (
    <div className="hidden lg:flex lg:w-[45%] bg-pattern bg-teal-700 overflow-hidden" style={surfaceStyle}>
      <div
        aria-hidden
        className={`pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full${accentColor ? '' : ' bg-orange-500'}`}
        style={accentStyle}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute -bottom-32 -left-28 h-80 w-80 rounded-full${surfaceColor ? '' : ' bg-teal-600'}`}
        style={shadeStyle}
      />
      <div className="relative z-10 flex flex-col justify-between p-12 w-full">
        {badge ? (
          <div className="flex items-center gap-3">
            {homeLink}
            <span className="rounded border border-white/30 bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              {badge}
            </span>
          </div>
        ) : (
          homeLink
        )}

        <div className="space-y-8">
          <div>
            <h2 className={`text-3xl font-bold text-white leading-tight mb-3${headingClassName ? ` ${headingClassName}` : ''}`}>{title}</h2>
            <p className="text-teal-50 text-base leading-relaxed max-w-sm">{description}</p>
          </div>

          <div className="space-y-4">
            {features.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-teal-100">{footer}</div>
      </div>
    </div>
  );
}
